import React, { useState, useEffect, useRef } from 'react'
import { isMatch, isEmpty } from 'lodash'
import { ChevronDown, ChevronUp } from 'react-feather'
import OutsideClickHandler from 'react-outside-click-handler'
import classNames from 'classnames'
import messages from 'utils/messages'
import Accordion from '../../../../../../components/Accordion/Accordion'
import { Scrollbars } from 'react-custom-scrollbars'
import { FormattedMessage } from 'react-intl'
import { DatePicker } from './DatePicker'
import { SearchIcon, CloseIcon, BxsDownloadIcon } from '../../../../../../images/common'
import { RELATIVE_TIME_RANGE_OPTIONS } from './DatePicker'
import { VuiPopOver } from '@topkamera/viso-ui'

const SelectTimeRangeMenu = ({
  options = [],
  selectedOption = {},
  menuOffset = 'left',
  onOptionSelected = (val) => null,
  onClose = (val) => null,
  withDashboardDefault = true,
  isOpen = false,
  anchorRef = null,
  position = 'bottom',
  alignment = 'start',
  onScrollOverFlow = 'stick',
  scrollContainerClassName = 'viso-scroll-wrap'
}) => {

  return (
    <VuiPopOver
      onClose={onClose}
      elementId='popoverRoot'
      isOpen={isOpen}
      anchorRef={anchorRef}
      alignment={alignment}
      position={position}
      animation='roll'
      onScrollOverFlow={onScrollOverFlow}
      scrollContainerClassName={scrollContainerClassName}
    >
      <div
        className={classNames(
          'dashboard-time-range-menu dashboard-time-range-menu--popover', 
          `dashboard-time-range-menu--popover--${position}-${alignment}`
        )}
        onClick={event => event.stopPropagation()}
        style={{ overscrollBehavior: 'contain' }}
      >
        <DatePicker
          selectedDate={selectedOption}
          relativeTimeOptions={[...options.length > 0 ? options : RELATIVE_TIME_RANGE_OPTIONS]}
          hasDashboardDefaultOption={withDashboardDefault}
          onApplySelectedDate={(timeRange) => {
            if (isEmpty(timeRange)) {
              return onOptionSelected({})
            }
            return onOptionSelected(timeRange)
          }}
        />
      </div>
    </VuiPopOver>
  )
}

const SelectSimpleMenu = ({
  options = [],
  onOptionSelected = (val) => null,
  onClose = (val) => null,
  onSearch = (val) => null,
  isSearchable = false,
  hasAccordions = [],
  anchorRef,
  isOpen
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (!searchValue) {
      setFilteredOptions(options);
    } else {
      setFilteredOptions(
        options.filter(option =>
          option.label && option.label.toLowerCase().includes(searchValue.toLowerCase())
        )
      )
    }
  }, [searchValue, options])

  useEffect(() => {
    if (isSearchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [])

  const handleSearch = (value) => {
    setSearchValue(value)
    onSearch && onSearch(value)
  }

  return (
    <VuiPopOver
      onClose={onClose}
      elementId='popoverRoot'
      isOpen={isOpen}
      anchorRef={anchorRef}
      offset={-30}
      fullWidth={{
        isFullWidth: true
      }}
      animation='roll'
    >
      <div
        className={classNames(
          'chart-editor-select-simple-menu',
          hasAccordions.length > 0 && 'chart-editor-select-simple-menu--accordion',
          isSearchable && 'chart-editor-select-simple-menu--search'
        )}
        onClick={event => event.stopPropagation()}
        style={{ overscrollBehavior: 'contain' }}
      >
        {isSearchable &&
          <div className='text-field text-field_nolabel text-field--search'>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search"
              className={classNames(
                'text-field__input text-field__input--search',
                hasAccordions.length > 0 && 'viso-border-none'
              )}
              onChange={(e) => handleSearch(e.target.value)}
              value={searchValue}
            />
            <div className="text-field__icon">{!searchValue && <SearchIcon />}</div>
            {searchValue && <span className="cursor text-field__icon text-field__icon_clickable" onClick={() => handleSearch('')}><CloseIcon /></span>}
          </div>
        }
        {hasAccordions.length > 0 ?
          <Accordion
            items={hasAccordions.map(accordion => ({
              title: accordion,
              contentRenderer: () =>
                <div>
                  {filteredOptions.filter(option => option.accordion === accordion).map((option, index) => (
                    <div
                      className='chart-editor-select-simple-menu-icon chart-editor-select-simple-menu-icon--view'
                      key={index}
                      onClick={() => onOptionSelected(option)}
                    >
                      <div className='chart-editor-select-simple-menu-icon__image'>
                        <BxsDownloadIcon />
                      </div>
                      <div className='chart-editor-select-simple-menu-icon__label'>
                        {option.label}
                      </div>
                    </div>
                  ))}
                </div>
            }))}
            fixedHeight={230}
            defaultOpenIndex={0}
            alwaysOpen={true}
          />
          :
          <Scrollbars
            autoHeight
            className="custom-scrollbars"
            autoHeightMax={214}
            universal
            renderThumbVertical={props => <div {...props} className="thumb-vertical" />}
            renderView={props => (
              <div
                {...props}
                style={{ ...props.style, overscrollBehavior: 'contain' }}
              />
            )}
          >
            {(filteredOptions && filteredOptions.length > 0) ?
              (filteredOptions.map((option, index) => (
                <div
                  className='chart-editor-select-simple-menu__option'
                  key={index}
                  onClick={() => onOptionSelected(option)}
                >
                  {option.label}
                </div>
              )))
              :
              (
                <div className='chart-editor-select-simple-menu__option'>
                  <FormattedMessage {...messages.noDataAvailable} />
                </div>
              )
            }
          </Scrollbars>
        }
      </div>
    </VuiPopOver>
  )
}

export const Select = ({
  label = '',
  hasBorder = false,
  placeholder = '',
  selectedOption = {},
  isSearchable = false,
  options = [],
  onChange = (val) => null,
  withSimpleMenu = true,
  timeRangePosition = 'bottom',
  timeRangeAlignment = 'start',
  withTimeRangeMenu = false,
  timeRangeOverflow = 'stick',
  timeRangeOverflowScrollClass = 'viso-scroll-wrap'
}) => {
  const [currentSelectedOption, setCurrentSelectedOption] = useState(selectedOption)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const hasAccordions = [...new Set(options.map(option => option.accordion).filter(Boolean))]
  const anchorRef = useRef(null)

  useEffect(() => {
    if (withTimeRangeMenu) {
      if (
        selectedOption?.start !== currentSelectedOption?.start ||
        selectedOption?.end !== currentSelectedOption?.end
      ) {
        setCurrentSelectedOption(selectedOption)
      }
    } else if (!withTimeRangeMenu) {
      if (!isMatch(selectedOption, currentSelectedOption)) {
        setCurrentSelectedOption(selectedOption)
      }
    }
  }, [selectedOption])

  const handleOnSelection = (newSelectedOption) => {
    setCurrentSelectedOption(newSelectedOption)
    onChange(newSelectedOption)
  }

  const handleToggleMenu = () => {
    setIsMenuOpen(prevState => !prevState)
  }

  const renderInputBoxContent = () => {
    if (currentSelectedOption.label) {
      return (
        <div> {currentSelectedOption.label} </div>
      )
    }
    if (placeholder) {
      return (
        <div className='chart-editor-select-input__box__placeholder'>
          {placeholder}
        </div>
      )
    }
    return null
  }

  return (
    <div className={classNames('chart-editor-select', {
      'chart-editor-border': hasBorder
    })}>
      {label &&
        <div className='chart-editor__label'>
          <label className="text-field__label1">{label}</label>
        </div>
      }
      <div
        className='chart-editor-select-input'
        onClick={handleToggleMenu}
        ref={anchorRef}
      >
        <div className='chart-editor-select-input__box'>
          {renderInputBoxContent()}
        </div>
        <div className='chart-editor-select-input__icon'>
          {isMenuOpen ? <ChevronUp /> : <ChevronDown />}
        </div>

        {(withSimpleMenu && !withTimeRangeMenu) &&
          <SelectSimpleMenu
            isSearchable={isSearchable}
            options={options}
            onOptionSelected={val => {
              handleOnSelection(val)
              handleToggleMenu()
            }}
            isOpen={isMenuOpen}
            anchorRef={anchorRef}
            onClose={handleToggleMenu}
            hasAccordions={hasAccordions}
          />
        }
        {withTimeRangeMenu &&
          <SelectTimeRangeMenu
            options={options}
            selectedOption={currentSelectedOption}
            onOptionSelected={val => {
              handleOnSelection(val)
              handleToggleMenu()
            }}
            onClose={handleToggleMenu}
            isOpen={isMenuOpen}
            anchorRef={anchorRef}
            position={timeRangePosition}
            alignment={timeRangeAlignment}
            onScrollOverFlow={timeRangeOverflow}
            scrollContainerClassName={timeRangeOverflowScrollClass}
          />
        }
      </div>
    </div>
  )
}